/**
 * Copyright (c) 2025, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import "./DebugBar.css";
import { SHOW_ERRORS, setShowErrors } from "./ErrorPreview";

// Developer-only overlay for stepping through the three workspaces without an
// account of each type, with or without any transactions, with an unfinished
// profile or no payout account, and with every error message shown in place.
// Opt in with ?debug=1 (and off again with ?debug=0, or the panel's "Turn off
// debug"); the choice is remembered in localStorage for the rest of the
// session, so a visitor never sees this unless they ask for it by hand.
// Deliberately not localised — it is a tool, not product copy.
//
// A round button in the corner showing what is being forced — the account
// type's letter, ∅ / ⏸ for no transactions / not live, ! while errors are
// shown — that opens a panel of switches on click. A panel rather than the
// strip it was: five switches no longer fit across a laptop screen.
// One labelled row of tabs.
const Group = ({ label, modes, testid }) => (
  <div className="Marketplace-Debug-group">
    <span className="Marketplace-Debug-label">{label}</span>
    <div className="Marketplace-Debug-tabs">
      {modes.map((t) => (
        <div
          className={"Marketplace-Debug-tab" + (t.active ? " Marketplace-Debug-tab--active" : "")}
          onClick={t.pick}
          data-testid={testid ? testid + "-" + t.id : undefined}
          key={t.id}
        >
          {t.label}
        </div>
      ))}
    </div>
  </div>
);

Group.propTypes = {
  label: PropTypes.string.isRequired,
  modes: PropTypes.array.isRequired,
  testid: PropTypes.string,
};

class DebugBar extends Component {
  constructor(props) {
    super(props);
    this.state = { open: false };
  }

  toggle = () => this.setState((s) => ({ open: !s.open }));

  render() {
    const vm = this.props.vm;
    const active = vm.debugPersonas.find((p) => p.active);
    const badge =
      (active ? active.label[0] : "D") +
      ({ none: "∅", notLive: "⏸" }[vm.debugTx] || "") +
      (SHOW_ERRORS ? "!" : "");
    return (
      <div className="Marketplace-Debug">
        {this.state.open && (
          <div className="Marketplace-Debug-panel" data-testid="debug-panel">
            <div className="Marketplace-Debug-head">
              <span className="Marketplace-Debug-title">DEBUG</span>
              <span className="Marketplace-Debug-off" onClick={vm.closeDebug}>
                Turn off debug
              </span>
            </div>
            <Group label="ACCOUNT TYPE" modes={vm.debugPersonas} />
            {/* None: what a brand-new account sees, payments and scheduled
                gifts loaded and empty. Not live: the same for a nonprofit not
                yet approved, with its CheckPlus rows unsettled. No reload — it
                only changes what the view models are handed. */}
            <Group label="TRANSACTIONS" modes={vm.debugTxModes} testid="debug-tx" />
            {/* Not connected: a nonprofit with no Stripe payout account — the
                dashboard's set-up alert and the profile's unconnected row. */}
            <Group label="PAYOUT" modes={vm.debugPayoutModes} testid="debug-payout" />
            {/* Incomplete: the profile nudge on the dashboard, even with no
                organisation signed in to have unanswered questions. */}
            <Group label="PROFILE" modes={vm.debugProfileModes} testid="debug-profile" />
            {/* The one switch that reloads: see setShowErrors. */}
            <Group
              label="ERRORS"
              testid="debug-errors"
              modes={[
                { id: "hidden", label: "Hidden", active: !SHOW_ERRORS, pick: () => SHOW_ERRORS && setShowErrors(false) },
                { id: "shown", label: "Shown", active: SHOW_ERRORS, pick: () => !SHOW_ERRORS && setShowErrors(true) },
              ]}
            />
          </div>
        )}
        <button
          type="button"
          className={"Marketplace-Debug-handle" + (this.state.open ? " Marketplace-Debug-handle--open" : "")}
          onClick={this.toggle}
          aria-expanded={this.state.open}
          aria-label="Debug switches"
          data-testid="debug-handle"
        >
          {this.state.open ? "×" : badge}
        </button>
      </div>
    );
  }
}

DebugBar.propTypes = {
  vm: PropTypes.object.isRequired,
};

export default DebugBar;
